/**
 * Pub data parsed from the authoritative visits.md file
 */
export interface Pub {
  /** UK postcode - unique identifier (e.g., "AB1 2CD") */
  postcode: string;

  /** Official pub name (e.g., "The Moon Under Water") */
  name: string;

  /** Town/city where pub is located */
  town: string;

  /** Full street address */
  address: string;

  /** Latitude (decimal degrees, -90 to 90) */
  lat: number;

  /** Longitude (decimal degrees, -180 to 180) */
  lng: number;

  /** Whether user has visited this pub */
  visited: boolean;

  /** Date of first visit (ISO 8601: "YYYY-MM-DD") or null if not visited */
  firstVisit: string | null;

  /** Date of most recent visit (ISO 8601: "YYYY-MM-DD") or null if not visited */
  lastVisit: string | null;

  /** Whether pub is permanently closed (optional, defaults to false) */
  closed?: boolean;

  /** Date pub closed (ISO 8601: "YYYY-MM-DD") or null (optional) */
  closedDate?: string | null;
}

/**
 * Array of pubs parsed from visits.md
 */
export type PubDatabase = Pub[];

/**
 * User preferences for cluster controls, persisted in localStorage
 */
export interface ClusterPreferences {
  /** Schema version for future migrations */
  version: 1;
  /** Whether marker clustering is enabled */
  clusteringEnabled: boolean;
}

/**
 * Default cluster preferences for first-time users
 */
export const DEFAULT_PREFERENCES: ClusterPreferences = {
  version: 1,
  clusteringEnabled: true,
} as const;

/**
 * User preferences for location tracking, persisted in localStorage
 * Stored under key 'le-spoons:location-preferences'
 */
export interface LocationPreferences {
  /** Schema version for future migrations */
  version: 1;
  /** Whether location tracking should auto-start on page load */
  locationEnabled: boolean;
}

/**
 * Default location preferences for first-time users
 */
export const DEFAULT_LOCATION_PREFERENCES: LocationPreferences = {
  version: 1,
  locationEnabled: false,
} as const;

/**
 * Error codes returned by the browser Geolocation API
 */
export type LocationErrorCode = 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT';

/**
 * A typed geolocation error with a user-readable message
 */
export interface LocationError {
  code: LocationErrorCode;
  userMessage: string;
}

/** Fixed compact cluster radius in pixels. */
export const CLUSTER_RADIUS = 40;
